import { useEffect, useState } from "react";
import { NavLink, useLoaderData, useRevalidator } from "react-router-dom";
import { Avatar } from "../components/Avatar";
import { GroupHeader, Panel, PanelHead } from "../components/Panel";
import { PriorityBadge } from "../components/Priority";
import { TextInput } from "../components/Field";
import { Icon } from "../components/Icon";
import { tabClass } from "../components/Tabs";
import { addMember, addMemberFromHub, createTag, fetchTeamDirectory, setMemberLeft, updateMember, updateTag } from "../data/api";
import { formatIsoDate } from "../../../src/lib/dates";
import { HubPeoplePicker } from "../components/HubPeoplePicker";
import { MemberForm } from "../components/MemberForm";
import { capacityFor } from "../../../src/shared/capacity";
import type { DirectoryState } from "../components/HubPeoplePicker";
import { useTags } from "../data/TagsContext";
import { groupTags } from "../components/TagPicker";
import { SLA_PRIORITIES, describeSla } from "../../../src/shared/sla";
import { TAG_KINDS, tagKind } from "../../../src/shared/tags";
import type { Tag, TeamMember } from "../data/types";

function subNavClass({ isActive }: { isActive: boolean }): string {
  return tabClass(isActive);
}

function SettingsFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-[900px] px-6 py-8">
      <h1 className="mb-4 text-display-xs font-semibold text-gray-900">Settings</h1>
      <nav className="mb-6 flex gap-6 border-b border-[var(--border)]">
        <NavLink to="/settings/sla" className={subNavClass}>
          SLA policy
        </NavLink>
        <NavLink to="/settings/team" className={subNavClass}>
          Team
        </NavLink>
        <NavLink to="/settings/tags" className={subNavClass}>
          Tags
        </NavLink>
      </nav>
      {children}
    </div>
  );
}

export function SettingsSla() {
  return (
    <SettingsFrame>
      <Panel labelledBy="sla-h">
        <PanelHead id="sla-h" title="Response and resolution targets" />
        <div className="grid grid-cols-[80px_minmax(0,1fr)_minmax(0,1fr)] gap-3 border-b border-[var(--border)] bg-gray-100 px-4 py-2 text-xs font-medium text-[var(--text-2)]">
          <span>Priority</span>
          <span>First response</span>
          <span>Resolution</span>
        </div>
        {SLA_PRIORITIES.map((p) => {
          const t = describeSla(p);
          return (
            <div
              key={p}
              className="grid grid-cols-[80px_minmax(0,1fr)_minmax(0,1fr)] items-center gap-3 border-b border-[var(--border)] px-4 py-3 text-sm last:border-b-0"
            >
              <PriorityBadge priority={p} />
              <span>{t.firstResponse}</span>
              <span>{t.resolution}</span>
            </div>
          );
        })}
      </Panel>
      <p className="mt-3 text-xs text-[var(--text-2)]">
        A commitment counts as at risk when it is due today or overdue and not yet resolved.
      </p>
    </SettingsFrame>
  );
}

export function SettingsTeam() {
  const team = useLoaderData() as TeamMember[];
  const active = team.filter((m) => m.leftAt === null);
  const departed = team.filter((m) => m.leftAt !== null);
  const [rejoinError, setRejoinError] = useState<string | null>(null);
  const revalidator = useRevalidator();
  // Adding is from the Hub by default; "manual" is the free-text fallback.
  const [adding, setAdding] = useState<false | "hub" | "manual">(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [directory, setDirectory] = useState<DirectoryState>({ kind: "loading" });

  // Loading the Hub directory also reconciles the roster's Hub links server
  // side (photos, members matched by email), so the roster is re-read once
  // it answers. Loaded on arrival, not on "Add member", for that reason.
  useEffect(() => {
    let live = true;
    fetchTeamDirectory()
      .then((d) => {
        if (!live) return;
        setDirectory(d.people ? { kind: "ready", people: d.people } : { kind: "error", reason: d.error ?? "unknown error" });
        if (d.people && d.changed > 0) revalidator.revalidate();
      })
      .catch(async (e: unknown) => {
        const reason = e instanceof Response ? await e.text() : e instanceof Error ? e.message : "";
        if (live) setDirectory({ kind: "error", reason: reason || "request failed" });
      });
    return () => {
      live = false;
    };
    // Once per visit: revalidator changes identity on every render.
  }, []);

  const refresh = () => {
    revalidator.revalidate();
    setAdding(false);
    setEditingId(null);
  };

  // After an add, mark the person as taken right away: the re-fetch is best
  // effort, and a failed one must not leave them on offer for a 409.
  const markAdded = (hubUserId: string, memberId: string) => {
    setDirectory((d) => (d.kind === "ready" ? { kind: "ready", people: d.people.map((p) => (p.hubUserId === hubUserId ? { ...p, memberId } : p)) } : d));
    refresh();
    fetchTeamDirectory()
      .then((d) => {
        if (d.people) setDirectory({ kind: "ready", people: d.people });
      })
      .catch((e: unknown) => console.warn("[team] directory refresh after add failed", e));
  };

  return (
    <SettingsFrame>
      <Panel labelledBy="team-h">
        <PanelHead id="team-h" title="Customer Engineering" count={active.length}>
          {!adding && (
            <button
              type="button"
              onClick={() => {
                setAdding("hub");
                setEditingId(null);
              }}
              className="rounded-sm px-2 py-1 text-xs font-medium text-brand-600 hover:text-brand-700 focus-visible:focus-ring"
            >
              Add member
            </button>
          )}
        </PanelHead>
        <div className="grid grid-cols-[minmax(0,1fr)_130px_minmax(0,1fr)_28px] gap-3 border-b border-[var(--border)] bg-gray-100 px-4 py-2 text-xs font-medium text-[var(--text-2)]">
          <span>Name</span>
          <span>Role</span>
          <span>Accounts</span>
          <span />
        </div>

        {active.map((m) =>
          editingId === m.id ? (
            <MemberForm
              key={m.id}
              initial={{ name: m.name, role: m.role, email: m.email ?? "", capacityDaysPerWeek: m.capacityDaysPerWeek }}
              onCancel={() => setEditingId(null)}
              onSave={async (v) => {
                await updateMember(m.id, v);
                refresh();
              }}
              onLeave={async () => {
                await setMemberLeft(m.id, true);
                refresh();
              }}
            />
          ) : (
            <div
              key={m.id}
              className="group grid grid-cols-[minmax(0,1fr)_130px_minmax(0,1fr)_28px] items-center gap-3 border-b border-[var(--border)] px-4 py-3 text-sm last:border-b-0"
            >
              <span className="flex min-w-0 items-center gap-2">
                <Avatar owner={m} size="sm" />
                <span className="min-w-0">
                  <span className="block truncate">{m.name}</span>
                  {m.email && <small className="block truncate text-xs text-[var(--text-2)]">{m.email}</small>}
                </span>
              </span>
              <span className="text-[var(--text-2)]">
                {m.role}
                <small className="block text-xs">
                  {capacityFor(m)} d/wk{m.capacityDaysPerWeek === null ? " · default" : ""}
                </small>
              </span>
              <span className="truncate text-[var(--text-2)]">{m.accountsLed.join(", ") || "—"}</span>
              <button
                type="button"
                onClick={() => {
                  setEditingId(m.id);
                  setAdding(false);
                }}
                aria-label={`Edit ${m.name}`}
                className="rounded p-0.5 text-[var(--text-2)] opacity-0 transition-opacity hover:text-[var(--text)] focus-visible:focus-ring group-hover:opacity-100"
              >
                <Icon name="edit" />
              </button>
            </div>
          ),
        )}

        {departed.length > 0 && (
          <>
            <GroupHeader label="Left the team" count={departed.length} />
            {departed.map((m) => (
              <div key={m.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-[var(--border)] px-4 py-3 text-sm last:border-b-0">
                <span className="flex min-w-0 items-center gap-2 text-[var(--text-2)]">
                  <Avatar owner={m} size="sm" />
                  <span className="truncate">{m.name}</span>
                  <small className="text-xs">left {formatIsoDate(m.leftAt ?? "")}</small>
                </span>
                <button
                  type="button"
                  onClick={() =>
                    void setMemberLeft(m.id, false)
                      .then(refresh)
                      .catch(async (e: unknown) => setRejoinError(e instanceof Response ? await e.text() : e instanceof Error ? e.message : "Could not update member"))
                  }
                  aria-label={`Rejoin ${m.name}`}
                  className="rounded-sm px-2 py-1 text-xs font-medium text-brand-600 hover:text-brand-700 focus-visible:focus-ring"
                >
                  Rejoin
                </button>
              </div>
            ))}
            {rejoinError && (
              <p role="alert" className="px-4 py-2 text-xs text-error-600">
                {rejoinError}
              </p>
            )}
          </>
        )}

        {adding === "hub" && (
          <HubPeoplePicker
            directory={directory}
            onManual={() => setAdding("manual")}
            onCancel={() => setAdding(false)}
            onAdd={async (hubUserId, role) => {
              const member = await addMemberFromHub(hubUserId, role);
              markAdded(hubUserId, member.id);
            }}
          />
        )}

        {adding === "manual" && (
          <MemberForm
            initial={{ name: "", role: "Engineer", email: "", capacityDaysPerWeek: null }}
            onCancel={() => setAdding(false)}
            onSave={async (v) => {
              await addMember(v);
              refresh();
            }}
          />
        )}
      </Panel>
    </SettingsFrame>
  );
}

/**
 * Settings › Tags: one panel per tag kind (Features today), rows under their
 * group headers. Hiding retires a tag — it stops being offered when tagging
 * but stays on requests that have it — so there is no delete. Admin-only like
 * the rest of Settings; the /api/tags writes refuse non-admins server side.
 */
export function SettingsTags() {
  const initial = useLoaderData() as Tag[];
  const revalidator = useRevalidator();
  const { reload } = useTags();
  const [adding, setAdding] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Kinds the UI knows first, then any kind that only exists in the data.
  const kinds = [...TAG_KINDS.map((k) => k.id), ...new Set(initial.map((t) => t.kind))].filter((k, i, a) => a.indexOf(k) === i);

  const refresh = () => {
    revalidator.revalidate();
    reload();
    setAdding(null);
    setEditingId(null);
  };

  const toggleHidden = async (t: Tag) => {
    setBusyId(t.id);
    try {
      await updateTag(t.id, { hidden: !t.hidden });
      refresh();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <SettingsFrame>
      <div className="grid gap-5">
        {kinds.map((kindId) => {
          const kind = tagKind(kindId);
          const tags = initial.filter((t) => t.kind === kindId);
          const groups = [...new Set(tags.map((t) => t.group).filter((g) => g !== ""))];
          const headId = `tags-${kindId}-h`;
          return (
            <Panel key={kindId} labelledBy={headId}>
              <PanelHead id={headId} title={kind.plural} count={tags.length}>
                {adding !== kindId && (
                  <button
                    type="button"
                    onClick={() => {
                      setAdding(kindId);
                      setEditingId(null);
                    }}
                    className="rounded-sm px-2 py-1 text-xs font-medium text-brand-600 hover:text-brand-700 focus-visible:focus-ring"
                  >
                    Add {kind.label.toLowerCase()}
                  </button>
                )}
              </PanelHead>

              {groupTags(tags).map(([group, list]) => (
                <div key={group || "—"}>
                  <GroupHeader label={group || "Other"} count={list.length} />
                  {list.map((t) =>
                    editingId === t.id ? (
                      <TagForm
                        key={t.id}
                        initial={{ name: t.name, group: t.group }}
                        groups={groups}
                        onCancel={() => setEditingId(null)}
                        onSave={async (v) => {
                          await updateTag(t.id, v);
                          refresh();
                        }}
                      />
                    ) : (
                      <div
                        key={t.id}
                        className={`group grid grid-cols-[minmax(0,1fr)_auto_auto_28px] items-center gap-3 border-b border-[var(--border)] px-4 py-2.5 text-sm last:border-b-0 ${t.hidden ? "text-[var(--text-3)]" : ""}`}
                      >
                        <span className="truncate">{t.name}</span>
                        {t.hidden ? <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-[var(--text-2)]">Hidden</span> : <span />}
                        <button
                          type="button"
                          disabled={busyId === t.id}
                          onClick={() => void toggleHidden(t)}
                          className="rounded-sm px-2 py-1 text-xs font-medium text-[var(--text-2)] hover:text-[var(--text)] focus-visible:focus-ring disabled:opacity-50"
                        >
                          {t.hidden ? "Show" : "Hide"}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(t.id);
                            setAdding(null);
                          }}
                          aria-label={`Edit ${t.name}`}
                          className="rounded p-0.5 text-[var(--text-2)] opacity-0 transition-opacity hover:text-[var(--text)] focus-visible:focus-ring group-hover:opacity-100"
                        >
                          <Icon name="edit" />
                        </button>
                      </div>
                    ),
                  )}
                </div>
              ))}

              {adding === kindId && (
                <TagForm
                  initial={{ name: "", group: "" }}
                  groups={groups}
                  onCancel={() => setAdding(null)}
                  onSave={async (v) => {
                    await createTag({ kind: kindId, ...v });
                    refresh();
                  }}
                />
              )}
            </Panel>
          );
        })}
      </div>
      <p className="mt-3 text-xs text-[var(--text-2)]">
        Hiding a tag stops it being offered on new and existing requests. Requests that already carry it keep it, and it stays filterable.
      </p>
    </SettingsFrame>
  );
}

function TagForm({
  initial,
  groups,
  onSave,
  onCancel,
}: {
  initial: { name: string; group: string };
  /** Existing group names, offered as suggestions; a new one can be typed. */
  groups: string[];
  onSave: (v: { name: string; group: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial.name);
  const [group, setGroup] = useState(initial.group);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listId = `tag-groups-${initial.name || "new"}`;

  return (
    <div className="border-b border-[var(--border)] px-4 py-3">
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-3">
        <TextInput aria-label="Tag name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
        <TextInput aria-label="Tag group" list={listId} value={group} onChange={(e) => setGroup(e.target.value)} placeholder="Group (optional)" />
        <datalist id={listId}>
          {groups.map((g) => (
            <option key={g} value={g} />
          ))}
        </datalist>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded-md px-2 py-1 text-xs font-medium text-[var(--text-2)] hover:text-[var(--text)] focus-visible:focus-ring">
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || name.trim() === ""}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await onSave({ name: name.trim(), group: group.trim() });
              } catch (e) {
                // The API client throws a Response whose body is the server's reason.
                const reason = e instanceof Response ? await e.text() : e instanceof Error ? e.message : "";
                setError(reason || "Could not save");
              } finally {
                setBusy(false);
              }
            }}
            className="rounded-md bg-brand-600 px-2 py-1 text-xs font-semibold text-white hover:bg-brand-700 focus-visible:focus-ring disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
      {error && <p className="mt-2 text-xs text-error-600">{error}</p>}
    </div>
  );
}
